<template>
    <div class="max-w-2xl mx-auto p-4">
        <h1 class="text-2xl font-semibold text-theme-base mb-2">Inventario</h1>
        <p class="text-sm text-theme-muted mb-6">
            Cómo llevas el stock de tus productos y cómo lo afectan tus ventas.
        </p>

        <div class="bg-theme-card rounded-xl p-6 flex flex-col gap-8">
            <section v-if="isLoaded" class="flex flex-col gap-3">
                <h2 class="text-lg font-semibold text-theme-base">
                    Modo de inventario
                </h2>
                <button
                    v-for="option in optionsWithState"
                    :key="option.value"
                    type="button"
                    :disabled="!canChangeMode || isSaving"
                    class="flex flex-col-reverse gap-2 rounded-lg border p-4 text-left transition disabled:cursor-not-allowed sm:flex-row sm:items-start sm:justify-between sm:gap-4"
                    :class="
                        option.value === inventoryMode
                            ? 'border-theme-primary bg-theme-hover'
                            : 'border-theme-border hover:bg-theme-hover'
                    "
                    @click="selectMode(option)"
                >
                    <span class="flex flex-1 flex-col gap-0.5">
                        <span
                            class="font-medium"
                            :class="
                                option.value === inventoryMode
                                    ? 'text-theme-primary'
                                    : 'text-theme-base'
                            "
                        >
                            {{ option.icon }} {{ option.label }}
                        </span>
                        <span class="text-sm text-theme-muted">
                            {{ option.description }}
                        </span>
                        <span
                            v-if="option.state === states.needsStores"
                            class="text-xs text-theme-warning"
                        >
                            Crea tu primera sucursal para usar este modo.
                        </span>
                    </span>
                    <span
                        class="flex-shrink-0 whitespace-nowrap text-xs font-medium"
                        :class="
                            option.value === inventoryMode
                                ? 'text-theme-success'
                                : 'text-theme-muted'
                        "
                    >
                        {{ optionStatus(option) }}
                    </span>
                </button>
                <router-link
                    v-if="showCreateStoreLink"
                    to="/app/stores/record"
                    class="text-sm text-theme-primary underline"
                >
                    Crear sucursal
                </router-link>
            </section>

            <section
                v-if="isLoaded && inventoryMode !== modes.none"
                class="flex flex-col gap-3"
            >
                <h2 class="text-lg font-semibold text-theme-base">Ventas</h2>
                <label class="flex items-center gap-2 cursor-pointer">
                    <custom-checkbox
                        :checked="settings.allowStockDecrementOnOrders"
                        :disabled="!canUpdateInventorySettings"
                        @change="
                            settings.allowStockDecrementOnOrders =
                                !settings.allowStockDecrementOnOrders
                        "
                    />
                    <span class="text-sm text-theme-base">
                        Descontar inventario al completar una orden
                    </span>
                </label>

                <div v-if="inventoryMode === modes.merchant">
                    <label class="flex items-center gap-2 cursor-pointer">
                        <custom-checkbox
                            :checked="settings.ordersNeedMerchant"
                            :disabled="!canUpdateInventorySettings"
                            @change="
                                settings.ordersNeedMerchant =
                                    !settings.ordersNeedMerchant
                            "
                        />
                        <span class="text-sm text-theme-base">
                            Exigir tienda asignada para completar órdenes del
                            checkout
                        </span>
                    </label>
                    <p class="mt-1 ml-7 text-xs text-theme-muted">
                        Solo aplica a órdenes creadas desde el catálogo público.
                        Las órdenes creadas desde el portal ya deciden esto al
                        momento de crearse.
                    </p>
                </div>

                <div
                    v-if="canUpdateInventorySettings"
                    class="flex justify-end pt-2"
                >
                    <app-button :loading="isLoading" @click="save">
                        Actualizar
                    </app-button>
                </div>
            </section>
        </div>

        <inventory-mode-dialog
            v-model="isDialogOpen"
            :current-mode="inventoryMode"
            :target-mode="targetMode"
            :stores="stores"
            :is-saving="isSaving"
            @confirm="changeMode"
        />
    </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";

import {
    INVENTORY_MODES,
    INVENTORY_MODE_OPTIONS,
    INVENTORY_MODE_STATES,
    inventoryModeState
} from "@/common/fields/inventory-modes";
import { isPlanManaged } from "@/common/fields/plan-features";

import InventoryModeDialog from "@/components/inventories/InventoryModeDialog.vue";

const STORES_PAGE_SIZE = 50;

export default {
    name: "InventorySettingsPage",
    components: { InventoryModeDialog },
    layout: "app",
    data: () => ({
        isLoading: false,
        isLoaded: false,
        isSaving: false,
        isDialogOpen: false,
        inventoryMode: INVENTORY_MODES.none,
        targetMode: null,
        stores: [],
        settings: {
            allowStockDecrementOnOrders: true,
            ordersNeedMerchant: true
        }
    }),
    computed: {
        ...mapGetters("control", ["backgroundColor"]),
        ...mapState("auth", ["featureFlags", "permissions"]),
        modes() {
            return INVENTORY_MODES;
        },
        states() {
            return INVENTORY_MODE_STATES;
        },
        canUpdateInventorySettings() {
            return this.$ability.hasPermission(
                "tenant:UpdateInventorySettings"
            );
        },
        canChangeMode() {
            return this.$ability.hasPermission("tenant:UpdateInventoryMode");
        },
        optionsWithState() {
            const hasPermission = (slug) => this.$ability.hasPermission(slug);
            return INVENTORY_MODE_OPTIONS.map((option) => ({
                ...option,
                state: inventoryModeState(
                    option.value,
                    hasPermission,
                    this.stores.length
                )
            }));
        },
        showCreateStoreLink() {
            return (
                this.optionsWithState.some(
                    (option) =>
                        option.state === INVENTORY_MODE_STATES.needsStores
                ) && this.$ability.hasPermission("store:Create")
            );
        }
    },
    async mounted() {
        const verifyPagePermission =
            this.$ability.verifyPagePermission.bind(this);
        if (
            !(await verifyPagePermission(
                "tenant:GetInventorySettings",
                "/app/settings"
            ))
        )
            return;
        await this.loadSettings();
    },
    methods: {
        ...mapActions("tenants", [
            "getTenantInventorySettings",
            "updateTenantInventorySettings",
            "changeTenantInventoryMode"
        ]),
        ...mapActions("stores", ["listStoresPaginated"]),
        ...mapActions("auth", ["applyFeatureFlags"]),
        ...mapActions("platformSubscriptions", ["openPlanUpgrade"]),
        async showError(error) {
            this.$sound.play("error");
            await this.$swal.fire({
                background: this.backgroundColor,
                title: error.title,
                text: error.message,
                icon: error.icon
            });
        },
        optionStatus(option) {
            if (option.value === this.inventoryMode) return "✓ Activo";
            if (option.state === INVENTORY_MODE_STATES.locked) {
                return "🔒 No incluido en tu plan";
            }
            return "";
        },
        async loadStores() {
            if (!this.$ability.hasPermission("store:List")) return [];
            const { items } = await this.listStoresPaginated({
                complete: false,
                itemsByPage: STORES_PAGE_SIZE,
                page: 1
            });
            return items || [];
        },
        async loadSettings() {
            try {
                const [data, stores] = await this.$loading.wrap(() =>
                    Promise.all([
                        this.getTenantInventorySettings(),
                        this.loadStores()
                    ])
                );
                this.inventoryMode = data.inventoryMode || INVENTORY_MODES.none;
                this.settings.allowStockDecrementOnOrders =
                    data.allowStockDecrementOnOrders;
                this.settings.ordersNeedMerchant = data.ordersNeedMerchant;
                this.stores = stores;
                this.isLoaded = true;
            } catch (error) {
                await this.showError(error);
            }
        },
        openUpgrade(feature) {
            if (!isPlanManaged(this.permissions)) return;
            this.openPlanUpgrade({ kind: "feature", feature });
        },
        selectMode(option) {
            if (option.value === this.inventoryMode) return;
            if (option.state === INVENTORY_MODE_STATES.locked) {
                this.openUpgrade(option.upgradeFeature);
                return;
            }
            if (option.state === INVENTORY_MODE_STATES.needsStores) return;
            this.targetMode = option.value;
            this.isDialogOpen = true;
        },
        async changeMode(payload) {
            this.isSaving = true;
            try {
                await this.changeTenantInventoryMode(payload);
                this.inventoryMode = payload.inventoryMode;
                this.applyFeatureFlags({
                    ...this.featureFlags,
                    inventoryMode: payload.inventoryMode
                });
                this.isSaving = false;
                this.isDialogOpen = false;
                this.$sound.play();
                this.$toast.success({
                    title: "Modo de inventario actualizado"
                });
            } catch (error) {
                this.isSaving = false;
                this.isDialogOpen = false;
                if (error.statusCode === 403) {
                    const option = INVENTORY_MODE_OPTIONS.find(
                        (item) => item.value === payload.inventoryMode
                    );
                    if (option && isPlanManaged(this.permissions)) {
                        this.openUpgrade(option.upgradeFeature);
                        return;
                    }
                }
                await this.showError(error);
            }
        },
        async save() {
            this.isLoading = true;
            try {
                await this.$loading.wrap(() =>
                    this.updateTenantInventorySettings({
                        allowStockDecrementOnOrders:
                            this.settings.allowStockDecrementOnOrders,
                        ordersNeedMerchant: this.settings.ordersNeedMerchant
                    })
                );
                this.isLoading = false;
                this.$sound.play();
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: "Actualización exitosa",
                    icon: "success",
                    showCloseButton: true,
                    timer: 3000
                });
            } catch (error) {
                this.isLoading = false;
                await this.showError(error);
            }
        }
    }
};
</script>
